import { $Props } from '@Ravnur-Inc/core/typings/tsx';
import { Vue, Options, prop } from 'vue-class-component';
import './search-input.scss';

const CN = 'search-input';

type Mode = 'fly' | 'manual';

class Props {
  value = prop({ default: '' });
  showClear = prop({ default: true });
  loading = prop({ default: false });
  mode = prop<Mode>({ default: 'fly' });
}

type Emits = {
  onInput: (value: string) => void;
};

@Options({
  name: 'search-input',
  emits: ['input', 'update:value'],
  watch: {
    value: {
      immediate: true,
      handler(this: SearchInput) {
        this.handleValueChanged();
      },
    },
  },
})
export default class SearchInput extends Vue.with(Props) {
  declare $props: $Props<Props, Emits, 'value'> & Partial<HTMLInputElement>;
  private q = '';

  get isFlyMode() {
    return this.mode === 'fly';
  }

  get isManualMode() {
    return this.mode === 'manual';
  }

  public focus() {
    const { field } = this.$refs;
    if (field instanceof HTMLInputElement) {
      field.focus();
    }
  }

  protected handleValueChanged() {
    this.q = this.value;
  }

  render() {
    return (
      <div class={CN}>
        <input
          ref="field"
          class={`${CN}__field`}
          type="text"
          value={this.q}
          {...this.$attrs}
          onInput={this.isManualMode ? this.setQ : this.emitSearchEvent}
          onKeypress={this.isManualMode ? this.handleKeypressEvent : noop}
        />

        <r-button
          class={`${CN}__search-btn`}
          color={this.q && this.isManualMode ? 'primary' : 'black'}
          icon="search"
          mode={this.q && this.isManualMode ? 'standard' : 'frameless'}
          onclick={this.isManualMode ? this.handleSearchClick : noop}
        />

        {this.loading ? (
          <spinner class={`${CN}__spinner`} size="md" think={true} />
        ) : (
          this.renderClearButton()
        )}
      </div>
    );
  }

  private renderClearButton() {
    if (!this.showClear || !this.q) {
      return;
    }
    return (
      <r-button
        class={`${CN}__clear`}
        color="primary"
        icon="cancel"
        mode="frameless"
        onclick={this.emitClearEvent}
        size="lg"
      />
    );
  }

  private handleSearchClick(ev: Event) {
    if (this.isManualMode) {
      this.emitSearchEvent(ev);
    }
  }

  private handleKeypressEvent(ev: Event) {
    if (ev instanceof KeyboardEvent && ev.which === 13) {
      this.emitSearchEvent(ev);
    }
  }

  // https://stackoverflow.com/questions/50617865/vue-v-model-input-change-mobile-chrome-not-work
  private setQ({ target }: Event) {
    if (target instanceof HTMLInputElement) {
      this.q = target.value;
    }
  }

  private emitSearchEvent(ev: Event) {
    this.setQ(ev);
    this.updateValue(this.q);
  }

  private emitClearEvent() {
    this.updateValue('');
    this.q = '';
  }

  private updateValue(value: string) {
    this.$emit('input', value);
    this.$emit('update:value', value);
  }
}

function noop() {
  //
}
